import { BaseGeometry } from '../types/BaseGeometry';
import { ObjectData } from '../components/generic/types/ObjectFormTypes';
import { UpdateGeometryRequest } from '../types/Geometry';

// Converts backend basegeometry dtos to objectdata for the objectform component
export function convertGeometryToObjectData(geometry: BaseGeometry): ObjectData {
  const drawingType = (() => {
    const up = geometry.wkt?.toUpperCase() || "";
    if (up.includes("POLYGON")) return "Polygon";
    if (up.includes("LINE")) return "Line";
    return "Point";
  })();

  return {
    // Required fields
    id: geometry.id,
    wkt: geometry.wkt || "",
    name: geometry.name || "",
    streetName: geometry.streetName || "",
    
    // Numeric ids converted to strings for dropdowns
    job: geometry.contractId?.toString() || "",
    status: geometry.stateId?.toString() || "",
    type: geometry.strengthId?.toString() || "",
    typeDesc: geometry.typeDesc || "",
    qualityControl: geometry.qualityControlId?.toString() || "",
    warrantyFollowUp: geometry.guaranteeFollowId?.toString() || "",
    
    // Text fields
    orderName: geometry.orderNumber || "",
    networkName: geometry.networkNumber || "",
    measures: geometry.typeComment || "",
    lanes: geometry.lanes || "",
    levelOfReadiness: geometry.goals || "",
    description: geometry.description || "",
    qualityControlComments: geometry.extraText1 || "",
    warrantyFollowUpComments: geometry.extraText2 || "",
    
    // Dates and nullable fields
    plannedYear: geometry.plannedDate ? new Date(geometry.plannedDate) : null,
    pvm: geometry.plannedDate ? new Date(geometry.plannedDate) : null,
    agreedPvm: geometry.plannedDate ? new Date(geometry.plannedDate) : null,
    completedPvm: geometry.readyDate ? new Date(geometry.readyDate) : null,
    approvedPvm: geometry.approvedDate ? new Date(geometry.approvedDate) : null,
    measurementDate: geometry.measurementDate ? new Date(geometry.measurementDate) : null,
    modifier: geometry.modifier || null,
    modified: geometry.modified ? new Date(geometry.modified) : null,
    implementedComment: geometry.implementedComment || '',
    measurementPersons: geometry.measurementPersons || '',
    contractorWorkNumber: geometry.contractorWorkNumber || '',
    invoicingPvm: "",
    checkedToInvoiceDate: geometry.checkedToInvoiceDate ? new Date(geometry.checkedToInvoiceDate) : null,
    checkedToInvoiceUserName: geometry.checkedToInvoiceUserName || null,
    
    // Objects
    subscriber: geometry.subscriberId && geometry.subscriberName
      ? {
          id: geometry.subscriberId,
          name: geometry.subscriberName,
          email: geometry.subscriberEmail,
          organization1: geometry.subscriberOrganization1,
          organization2: geometry.subscriberOrganization2,
        }
      : null,
    
    foreman: geometry.foremanId && geometry.foremanName
      ? {
          id: geometry.foremanId,
          name: geometry.foremanName,
          email: geometry.foremanEmail,
        }
      : null,
    
    contractorForeman: geometry.contractorForemanId && geometry.contractorForemanName
      ? {
          id: geometry.contractorForemanId,
          name: geometry.contractorForemanName,
          email: geometry.contractorForemanEmail,
        }
      : null,
    
    projectManager: geometry.projectManagerId && geometry.projectManagerName
      ? {
          id: geometry.projectManagerId,
          name: geometry.projectManagerName,
          email: geometry.projectManagerEmail,
        }
      : null,
    
    drillLocation: geometry.drillLocation
      ? {
          id: geometry.drillLocation.id,
          xcoord: geometry.drillLocation.xcoord,
          ycoord: geometry.drillLocation.ycoord,
        }
      : null,
    
    // Arrays and file collections
    rolePermissions: geometry.rolePermissions?.map((rp) => ({
      roleId: rp.roleId,
      roleName: rp.roleName,
      accessLevel: rp.accessLevel,
    })) || [],
    
    existingFiles: geometry.files || [],
    existingQcFiles: geometry.qcFiles || [],
    fileIdsToKeep: geometry.files?.map((f) => f.id) || [],
    qcFileIdsToKeep: geometry.qcFiles?.map((f) => f.id) || [],
    
    // Form specific fields
    drawingType: drawingType,
    imageAttachment: null,
    coatingResearchReport: [],
    attachedFiles: [],
    plannedWorkSteps: [],
    userRightsType: geometry.roleAccessInUse ? "site-specific" : "general",
    targetContract: "",
    newObjectName: "",
    checkedToInvoice: Boolean(geometry.checkedToInvoice === 1),
    signingLocked: Boolean(geometry.signingLocked === 1),
  };
}

// Converts objectdata back to basegeometry format
export function convertObjectDataToBaseGeometry(
  objectData: ObjectData,
  originalGeometry: BaseGeometry
): BaseGeometry {
  return {
    ...originalGeometry,
    
    // Updated numeric fields
    contractId: objectData.job ? parseInt(objectData.job) : originalGeometry.contractId,
    stateId: objectData.status ? parseInt(objectData.status) : originalGeometry.stateId,
    
    // Updated text fields
    name: objectData.name,
    wkt: objectData.wkt,
    streetName: objectData.streetName,
    
    // Optional fields
    strengthId: objectData.type ? parseInt(objectData.type) : undefined,
    foremanId: objectData.foreman?.id || undefined,
    contractorForemanId: objectData.contractorForeman?.id || undefined,
    projectManagerId: objectData.projectManager?.id || undefined,
    subscriberId: objectData.subscriber?.id || undefined,
    qualityControlId: objectData.qualityControl ? parseInt(objectData.qualityControl) : undefined,
    guaranteeFollowId: objectData.warrantyFollowUp ? parseInt(objectData.warrantyFollowUp) : undefined,
    checkedToInvoice: objectData.checkedToInvoice ? 1 : 0,
    signingLocked: objectData.signingLocked ? 1 : 0,
    roleAccessInUse: objectData.userRightsType === "site-specific" ? 1 : 0,
    
    // String mappings
    orderNumber: objectData.orderName,
    networkNumber: objectData.networkName,
    typeComment: objectData.measures,
    lanes: objectData.lanes,
    goals: objectData.levelOfReadiness,
    description: objectData.description,
    foremanName: objectData.foreman?.name || undefined,
    foremanEmail: objectData.foreman?.email || undefined,
    contractorForemanName: objectData.contractorForeman?.name || undefined,
    contractorForemanEmail: objectData.contractorForeman?.email || undefined,
    projectManagerName: objectData.projectManager?.name || undefined,
    projectManagerEmail: objectData.projectManager?.email || undefined,
    subscriberName: objectData.subscriber?.name || undefined,
    subscriberEmail: objectData.subscriber?.email || undefined,
    extraText1: objectData.qualityControlComments,
    extraText2: objectData.warrantyFollowUpComments,
    
    // Date conversions
    plannedDate: objectData.pvm?.toISOString() || null,
    readyDate: objectData.completedPvm?.toISOString() || null,
    approvedDate: objectData.approvedPvm?.toISOString() || null,
    measurementDate: objectData.measurementDate?.toISOString() || null,
    checkedToInvoiceDate: objectData.checkedToInvoiceDate?.toISOString() || null,
    checkedToInvoiceUserName: objectData.checkedToInvoiceUserName,
    modifier: objectData.modifier,
    modified: objectData.modified?.toISOString() || null,
    implementedComment: objectData.implementedComment || '',
    measurementPersons: objectData.measurementPersons || null,
    contractorWorkNumber: objectData.contractorWorkNumber || null,
    
    // Nested objects
    drillLocation: objectData.drillLocation
      ? {
          id: objectData.drillLocation.id || 0,
          xcoord: objectData.drillLocation.xcoord || 0,
          ycoord: objectData.drillLocation.ycoord || 0,
          locationNumber: 0,
        }
      : null,
    
    // Arrays
    rolePermissions: objectData.rolePermissions?.map((rp) => ({
      roleId: rp.roleId,
      roleName: rp.roleName,
      roleDescription: rp.roleName,
      accessLevel: rp.accessLevel,
    })) || [],
  };
}

// Converts form objectdata to updategeometryrequest for api
export function convertObjectDataToUpdateRequest(
  objectData: ObjectData,
  currentUserId: number,
  currentUser: string,
  regularFiles: any[] | null,
  qcFiles: any[] | null
): UpdateGeometryRequest {
  return {
    // Core fields
    perceptionId: objectData.id,
    visible: 1,
    stateId: objectData.status ? parseInt(objectData.status) : null,
    contractId: null, 

    startDistance: null,
    endDistance: null,
    name: objectData.name,
    wkt: objectData.wkt,
    streetName: objectData.streetName,
    
    // Optional numeric fields
    typeId: objectData.type ? parseInt(objectData.type) : null,
    foremanId: objectData.foreman?.id || null,
    contractorForemanId: objectData.contractorForeman?.id || null,
    projectManagerId: objectData.projectManager?.id || null,
    subscriberId: objectData.subscriber?.id || null,
    qualityControlId: objectData.qualityControl ? parseInt(objectData.qualityControl) : null,
    qualityControlId2: null,
    guaranteeFollowId: objectData.warrantyFollowUp ? parseInt(objectData.warrantyFollowUp) : null,
    objectLen: null,
    hasRoadMarkings: 0,
    roleAccessInUse: objectData.userRightsType === "site-specific" ? 1 : 0,
    publishToApi: 1,
    
    // String mappings
    orderNumber: objectData.orderName,
    networkNumber: objectData.networkName,
    siteNumber: objectData.job, 
    typeComment: objectData.measures,
    lanes: objectData.lanes,
    goals: objectData.levelOfReadiness,
    description: objectData.description,
    foremanName: objectData.foreman?.name || null,
    projectManagerName: objectData.projectManager?.name || null,
    extraText1: objectData.qualityControlComments,
    extraText2: objectData.warrantyFollowUpComments,
    
    // Date conversions to iso strings
    startDate: null,
    plannedDate: objectData.pvm?.toISOString() || null,
    readyDate: objectData.completedPvm?.toISOString() || null,
    approvedDate: objectData.approvedPvm?.toISOString() || null,
    
    // Drill location object
    drillLocation:
      objectData.drillLocation &&
      (objectData.drillLocation.xcoord !== null ||
        objectData.drillLocation.ycoord !== null)
        ? {
            xcoord: objectData.drillLocation.xcoord,
            ycoord: objectData.drillLocation.ycoord,
          }
        : null,
    
    // Arrays and file handling
    rolePermissions:
      objectData.userRightsType === "site-specific"
        ? objectData.rolePermissions.map((rp) => ({
            roleId: rp.roleId,
            accessLevel: rp.accessLevel,
          }))
        : null,
    files: regularFiles,
    qcFiles: qcFiles,
    fileIdsToKeep: objectData.fileIdsToKeep.length > 0 ? objectData.fileIdsToKeep : null,
    qcFileIdsToKeep: objectData.qcFileIdsToKeep.length > 0 ? objectData.qcFileIdsToKeep : null,
    
    // Request metadata
    drawingType: objectData.drawingType,
    modifierUserId: currentUserId,
    modifierUserName: currentUser,
    checkedToInvoice: objectData.checkedToInvoice,
  };
}

